import { produce } from "immer";

import { generateContent } from "@app/services/zoltar";
import { batchPresignURLs } from "@app/services/s3";

import * as storiesApi from "@app/services/stories";
import { storyKey } from "@app/hooks/stories";

import { queryClient } from "./query";

export const createStorySlice = (set, get) => ({
  stories: [],
  setStories: (stories) => {
    set(
      produce((state) => {
        state.stories = stories.map((story) => {
          const stateStory = state.stories.find((s) => s.id === story.id);
          return stateStory ? { ...stateStory, ...story } : story;
        });
      }),
      undefined,
      "story/setStories",
    );
  },
  isFetchingStories: false,
  /**
   * Sets the fetching state for stories.
   *
   * @param {boolean} isFetching - Whether stories are being fetched.
   *
   * @returns {void}
   */
  setFetchingStories: (isFetching) =>
    set(
      {
        isFetchingStories: isFetching,
      },
      undefined,
      "story/setFetchingStories",
    ),
  isFetchingStory: {},
  /**
   * Sets the fetching state for a specific story.
   *
   * @param {string} id - The ID of the story.
   * @param {boolean} isFetching - Whether the story is being fetched.
   *
   * @returns {void}
   */
  setFetchingStory: (id, isFetching) =>
    set(
      produce((state) => {
        state.isFetchingStory[id] = isFetching;
      }),
      undefined,
      "story/setFetchingStory",
    ),
  /**
   * Retrieves a story from the state by its ID.
   *
   * @param {string} id - The ID of the story.
   *
   * @returns {Object|null} - The story object if found, or null.
   */
  getStory: (id) => queryClient.getQueryData(storyKey.detail(id)) ?? null,
  /**
   * Updates a story in the state.
   *
   * @param {string} id - The ID of the story.
   * @param {Object} update - The update to apply to the story.
   *
   * @returns {void}
   */
  updateStory: (id, update) => {
    set(
      produce((state) => {
        const index = state.stories.findIndex((story) => story.id === id);
        if (index !== -1) {
          state.stories[index] = { ...state.stories[index], ...update };
        } else {
          state.stories.push({ id, ...update });
        }
      }),
      undefined,
      "story/updateStory",
    );
  },
  /**
   * Generates images for the story with the given ID.
   *
   * @param {string} id - The ID of the story.
   * @param {boolean} [forceGenerate=false] - Whether to force image generation even if they already exist.
   *
   * @returns {Promise<void>}
   */
  generateStoryImages: async (id, forceGenerate = false) => {
    const story = get().getStory(id);

    if (!story) {
      console.error(`Story with id ${id} not found`);
      return;
    }

    if (story.personas?.some((p) => !p.avatarPrompt) || forceGenerate) {
      await get().generatePersonas(id);
    }

    const personasPromises = [];
    const visualsPromises = [];

    // FIXME: don't generate personas images if they already exist
    // if (
    //   story.personas?.some(
    //     (p) => !p.libraryId && !p.avatarId || !p.avatarUrl,
    //   ) ||
    //   forceGenerate
    // ) {
    //   personasPromises.push(get().generatePersonasImages(id));
    // }

    if (story.script?.some((step) => !step.visuals?.length) || forceGenerate) {
      visualsPromises.push(get().fetchVisuals(id));
    }

    await Promise.all(personasPromises);
    await Promise.all(visualsPromises);

    // Invalidate and refetch the story to get the latest data from the server
    await queryClient.invalidateQueries({ queryKey: storyKey.detail(id) });
  },
  isCheckStoryVisualsPending: {},
  /**
   * Sets the loading state for checking story visuals.
   *
   * @param {string} id - The ID of the story.
   * @param {boolean} isPending - Whether the check is pending.
   *
   * @returns {void}
   */
  setCheckStoryVisualsPending: (id, isPending) => {
    set(
      produce((state) => {
        state.isCheckStoryVisualsPending[id] = isPending;
      }),
      undefined,
      "story/setCheckStoryVisualsPending",
    );
  },
  lastVisualCheck: {},
  /**
   * Sets the last visual check timestamp for a story.
   *
   * @param {string} id - The ID of the story.
   * @param {number} timestamp - The timestamp of the last visual check.
   *
   * @returns {void}
   */
  setLastVisualCheck: (id, timestamp) => {
    set(
      produce((state) => {
        state.lastVisualCheck[id] = timestamp;
      }),
      undefined,
      "story/setLastVisualCheck",
    );
  },
  /**
   * Checks the visual URLs for a story.
   *
   * @param {Object} story - The story object to check.
   *
   * @returns {Promise<void>}
   */
  checkStoryVisualURLs: async (story) => {
    if (!story) {
      return;
    }

    get().setCheckStoryVisualsPending(story.id, true);
    get().setLastVisualCheck(story.id, +new Date());

    try {
      const randomVisual = story.script?.find(
        (step) => step.visuals && step.visuals.length > 0,
      )?.visuals[0]?.url;

      if (!randomVisual) {
        return;
      }

      const response = await fetch(randomVisual, {
        method: "HEAD",
      });

      if (!response.ok) {
        throw new Error(
          `Visual URL check failed for story ${id}: ${randomVisual}`,
        );
      }

      get().setCheckStoryVisualsPending(story.id, false);
    } catch (error) {
      get().updateStoryVisualURLs(story.id);
    }
  },
  /**
   * Updates the visual URLs for a story.
   *
   * @param {string} id - The ID of the story.
   *
   * @returns {Promise<void>}
   */
  updateStoryVisualURLs: async (id) => {
    const story = get().getStory(id);

    try {
      const personaVisuals =
        story?.personas?.map(({ avatarPath }) => avatarPath).filter(Boolean) ||
        [];
      const scriptVisuals =
        story?.script?.reduce((acc, { visuals }) => {
          if (visuals) {
            acc.push(...visuals.map(({ path }) => path));
          }
          return acc;
        }, []) || [];

      // const visualURLs = await batchPresignURLs([
      //   ...personaVisuals,
      //   ...scriptVisuals,
      // ]);

      const visualURLs = [];

      console.log(`Presigned URLs for story ${id}:`, visualURLs);

      // set(
      //   produce((state) => {
      //     const storyIndex = state.stories.findIndex(
      //       (story) => story.id === id,
      //     );

      //     if (storyIndex !== -1) {
      //       state.stories[storyIndex].personas.forEach((persona, idx) => {
      //         const url = visualURLs.find(
      //           (url) => url.filePath === persona.avatarPath,
      //         );

      //         if (url) {
      //           state.stories[storyIndex].personas[idx].avatarUrl =
      //             url.presignURL;
      //         }
      //       });

      //       state.stories[storyIndex].script.forEach((step, idx) => {
      //         state.stories[storyIndex].script[idx]?.visuals?.forEach(
      //           (visual, vIdx) => {
      //             const url = visualURLs.find(
      //               (url) => url.filePath === visual.path,
      //             );

      //             if (url) {
      //               state.stories[storyIndex].script[idx].visuals[vIdx].url =
      //                 url.presignURL;
      //             }
      //           },
      //         );
      //       });
      //     }
      //   }),
      //   undefined,
      //   "story/updateStoryVisualURLs",
      // );

      queryClient.setQueryData(storyKey.detail(id), (oldData) =>
        produce(oldData, (state) => {
          state.personas.forEach((persona, idx) => {
            const url = visualURLs.find(
              (url) => url.filePath === persona.avatarPath,
            );

            if (url) {
              state.personas[idx].avatarUrl = url.presignURL;
            }
          });

          state.script.forEach((step, idx) => {
            state.script[idx]?.visuals?.forEach((visual, vIdx) => {
              const url = visualURLs.find(
                (url) => url.filePath === visual.path,
              );

              if (url) {
                state.script[idx].visuals[vIdx].url = url.presignURL;
              }
            });
          });
        }),
      );

      // get().saveStory(id);

      get().setCheckStoryVisualsPending(story.id, false);
    } catch (error) {
      console.error(`Error updating visual URLs for story ${id}:`, error);
      get().setCheckStoryVisualsPending(story.id, false);
    }
  },
  /**
   * Updates a script item in a story.
   *
   * @param {string} storyId - The ID of the story.
   * @param {string} itemId - The ID of the script item to update.
   * @param {Object} update - The update to apply to the script item.
   *
   * @returns {void}
   */
  updateStoryScriptItem: (storyId, itemId, update) => {
    queryClient.setQueryData(storyKey.detail(storyId), (oldData) =>
      produce(oldData, (state) => {
        const itemIndex = state.script.findIndex((item) => item.id === itemId);
        if (itemIndex !== -1) {
          state.script[itemIndex] = {
            ...state.script[itemIndex],
            ...update,
          };
        }
      }),
    );
  },
  updateStoryPersonaImage: (storyId, libraryId, update) => {
    queryClient.setQueryData(storyKey.detail(storyId), (oldData) =>
      produce(oldData, (state) => {
        const itemIndex = state.personas.findIndex(
          (item) => item.libraryId === libraryId,
        );
        if (itemIndex !== -1) {
          state.personas[itemIndex] = {
            ...state.personas[itemIndex],
            ...update,
          };
        }
      }),
    );
  },
  updateStoryVisualImage: (storyId, stepId, visualId, update) => {
    queryClient.setQueryData(storyKey.detail(storyId), (oldData) =>
      produce(oldData, (state) => {
        const itemIndex = state.script.findIndex((item) => item.id === stepId);
        if (itemIndex !== -1) {
          state.script[itemIndex].visuals[visualId] = {
            ...state.script[itemIndex].visuals[visualId],
            libraryId: update?.libraryId,
            id: update?.id,
          };

          if (update.visualPrompt) {
            state.script[itemIndex].visualPrompt = update.visualPrompt;
          }
        }
      }),
    );
  },
});
